import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from './index';
import type { User } from './authSlice';

interface LoginRequest {
  username: string;
  password: string;
}

interface RegisterRequest {
  // Personal Information
  first_name: string;
  middle_name?: string;
  last_name: string;
  
  // Identity Information
  nin: string;
  date_of_birth: string; // YYYY-MM-DD format
  nationality?: string;
  
  // Contact Information
  email: string;
  phone: string;
  
  // Address Information
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code?: string;
  country?: string;
  
  // Employment (Optional)
  employer?: string;
  
  // Authentication
  password: string;
  confirm_password: string;
  
  // Legal
  accept_terms: boolean;
}

interface AuthResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL}/auth`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
    timeout: 30000, // 30 second timeout
  }),
  tagTypes: ['Auth'],
  endpoints: (builder) => ({
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => {
        console.log('🔑 Login API called with:', credentials);
        
        // OAuth2 form encoding with all required fields
        const encodedParams = new URLSearchParams();
        encodedParams.set('grant_type', '');
        encodedParams.set('username', credentials.username);
        encodedParams.set('password', credentials.password);
        encodedParams.set('scope', '');
        encodedParams.set('client_id', '');
        encodedParams.set('client_secret', '');
        
        const bodyString = encodedParams.toString();
        console.log('📋 OAuth2 encoded params:', bodyString);
        console.log('📋 Body string length:', bodyString.length);
        
        return {
          url: '/login',
          method: 'POST',
          body: bodyString,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        };
      },
      invalidatesTags: ['Auth'],
    }),
    
    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (userData) => ({
        url: '/register',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['Auth'],
    }),
    
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/logout',
        method: 'POST',
      }),
      invalidatesTags: ['Auth'],
    }),
    
    getCurrentUser: builder.query<User, void>({
      query: () => '/me',
      providesTags: ['Auth'],
    }),
    
    refreshToken: builder.mutation<AuthResponse, void>({
      query: () => ({
        url: '/refresh',
        method: 'POST',
      }),
      invalidatesTags: ['Auth'],
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useRefreshTokenMutation,
} = authApi;